import { errorBodySchema, type ErrorBody, type ErrorCode } from '@wodi/shared';
import type { z } from 'zod';

export type ClientErrorCode = ErrorCode | 'offline' | 'unexpected_response';
export type ErrorContext = NonNullable<ErrorBody['error']['context']>;

/** An API failure with a message fit to show the person as it is. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  /** On internal errors: the reference to quote when reporting the problem. */
  readonly requestId: string | null;
  /** IDs and codes to act on (ERROR_CONTEXT_SCHEMAS), e.g. the open visit's ID. */
  readonly context: ErrorContext | null;
  /** Field-level problems from `validation_failed`. */
  readonly details: readonly { path: string; message: string }[];

  constructor(
    status: number,
    code: ClientErrorCode,
    message: string,
    requestId?: string,
    extra: { context?: ErrorContext; details?: { path: string; message: string }[] } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId ?? null;
    this.context = extra.context ?? null;
    this.details = extra.details ?? [];
  }
}

/** The server's message, with the reference appended when there is one. */
export function errorMessage(message: string, requestId: string | undefined): string {
  return requestId === undefined ? message : `${message} Reference: ${requestId}`;
}

export const OFFLINE_MESSAGE = "Can't reach Wodi. Check the internet connection, then try again.";

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function api<T = undefined>(
  method: Method,
  path: string,
  options: { body?: unknown; schema?: z.ZodType<T>; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...options.headers, accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, init);
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (response.status === 204) return undefined as T;
  const json: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(json);
    if (parsed.success) {
      const { code, message, requestId, context, details } = parsed.data.error;
      throw new ApiError(response.status, code, errorMessage(message, requestId), requestId, {
        ...(context === undefined ? {} : { context }),
        ...(details === undefined ? {} : { details }),
      });
    }
    throw new ApiError(response.status, 'unexpected_response', 'Something went wrong. Try again.');
  }
  if (options.schema === undefined) return json as T;
  const parsed = options.schema.safeParse(json);
  if (!parsed.success)
    throw new ApiError(response.status, 'unexpected_response', 'Something went wrong. Try again.');
  return parsed.data;
}

const FILE_NAME = /filename="([^"]+)"/;

/**
 * Fetches a file and saves it through the browser. The file name comes from the server's
 * content-disposition header (which names no patient), else `fallbackName`. Returns the name.
 */
export async function downloadFile(
  url: string,
  init: RequestInit,
  fallbackName: string,
): Promise<string> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin', ...init });
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      const { code, message, requestId } = parsed.data.error;
      throw new ApiError(response.status, code, errorMessage(message, requestId), requestId);
    }
    throw new ApiError(response.status, 'unexpected_response', 'Something went wrong. Try again.');
  }
  const name =
    FILE_NAME.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Let the click start the download before the object URL goes away.
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1_000);
  }
  return name;
}
